import * as XLSX from 'xlsx';
import { toast } from 'react-hot-toast';
import { getErrorMessage } from '../../admin/utils/errorUtils.ts';
import {
  fetchModuleAuditEvents,
  type AuditEvent,
  type ModuleAuditEventListParams,
} from './moduleAudit.api.ts';

function formatWhen(iso: string): string {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString();
  } catch {
    return iso;
  }
}

function toExcelRows(rows: AuditEvent[]) {
  return rows.map((e) => ({
    When: formatWhen(e.created_at),
    Action: e.action,
    Resource: e.resource_type,
    'Resource Label': e.resource_label || '',
    'Resource ID': e.resource_id || '',
    Summary: e.summary,
    Actor: e.actor_username || '',
    'Actor Role': e.actor_role || '',
  }));
}

export type DownloadModuleAuditExcelOptions = {
  resourceType: string;
  /** Optional date / search filters (org is always forced server-side). */
  filters?: Omit<ModuleAuditEventListParams, 'resource_type' | 'page' | 'page_size'>;
  /** Filename prefix, e.g. "risk_audit". Default: module_audit_<resourceType>. */
  filenamePrefix?: string;
};

/** Fetch all pages for one resource_type in the current org and write Excel. */
export async function downloadModuleAuditExcel(options: DownloadModuleAuditExcelOptions): Promise<void> {
  const resourceType = String(options.resourceType || '').trim();
  if (!resourceType) {
    toast.error('resource_type is required');
    return;
  }

  const filterParams = options.filters ?? {};
  const rows: AuditEvent[] = [];
  let exportPage = 1;
  let totalPages = 1;
  do {
    const res = await fetchModuleAuditEvents({
      ...filterParams,
      resource_type: resourceType,
      page: exportPage,
      page_size: 100,
    });
    rows.push(...(res.events ?? []));
    totalPages = res.pagination?.total_pages || 1;
    exportPage += 1;
  } while (exportPage <= totalPages);

  if (rows.length === 0) {
    toast.error('No audit events to export for this module');
    return;
  }

  const worksheet = XLSX.utils.json_to_sheet(toExcelRows(rows));
  worksheet['!cols'] = [
    { wch: 22 }, { wch: 10 }, { wch: 18 }, { wch: 28 },
    { wch: 36 }, { wch: 40 }, { wch: 16 }, { wch: 14 },
  ];
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Audit Log');
  const timestamp = new Date().toISOString().split('T')[0];
  const prefix = (options.filenamePrefix || `module_audit_${resourceType}`).replace(/\s+/g, '_');
  XLSX.writeFile(workbook, `${prefix}_${timestamp}.xlsx`);
  toast.success('Audit Excel downloaded');
}

export function downloadModuleAuditExcelSafe(options: DownloadModuleAuditExcelOptions): Promise<void> {
  return downloadModuleAuditExcel(options).catch((err) => {
    toast.error(getErrorMessage(err, 'Failed to download audit Excel'));
  });
}
